import { type FC, type ReactNode } from 'react';
import { graphql, useFragment } from 'react-relay';
import classnames from 'classnames';

import { useScrollCarouselItem } from 'dibs-scroll-carousel/exports/useScrollCarouselItem';
import { Link } from 'dibs-elements/exports/Link';
import { getSrcSetStringXDescriptors } from 'dibs-image-utils/exports/srcSet';

import Gallery from 'dibs-icons/exports/legacy/Gallery';
import ArtworkFrame from 'dibs-icons/exports/legacy/ArtworkFrame';
import PlayButtonInverse from 'dibs-icons/exports/legacy/PlayButtonInverse';

import { type CarouselThumb_sortedPhoto$key } from './__generated__/CarouselThumb_sortedPhoto.graphql';
import { type CarouselThumb_videoVimeo$key } from './__generated__/CarouselThumb_videoVimeo.graphql';
import { type CarouselThumb_viewInRoomPhoto$key } from './__generated__/CarouselThumb_viewInRoomPhoto.graphql';

import styles from './CarouselThumb.scss';

const sortedPhotoFragment = graphql`
    fragment CarouselThumb_sortedPhoto on ItemPhoto {
        masterOrZoomPath
        alt
    }
`;

const videoVimeoFragment = graphql`
    fragment CarouselThumb_videoVimeo on VideoVimeo {
        thumbnail(size: w100h75, includePlayButton: false, pad: false)
    }
`;

const viewInRoomPhotoFragment = graphql`
    fragment CarouselThumb_viewInRoomPhoto on viewInRoomPhotos {
        src
        credits
    }
`;

type Size = 'xxSmall' | 'xSmall' | 'small' | 'medium' | 'large';

export const CarouselThumb: FC<
    | {
          type: 'photo';
          size?: Size;
          sortedPhoto: CarouselThumb_sortedPhoto$key;
          videoVimeo: null;
          viewInRoomPhoto: null;
      }
    | {
          type: 'video';
          size?: Size;
          sortedPhoto: null;
          videoVimeo: CarouselThumb_videoVimeo$key;
          viewInRoomPhoto: null;
      }
    | {
          type: 'viewInRoom';
          size?: Size;
          sortedPhoto: null;
          videoVimeo: null;
          viewInRoomPhoto: CarouselThumb_viewInRoomPhoto$key;
      }
    | {
          type: 'requestMore';
          size?: Size;
          sortedPhoto: null;
          videoVimeo: null;
          viewInRoomPhoto: null;
      }
> = ({
    type,
    size = 'large',
    sortedPhoto: sortedPhotoRef,
    videoVimeo: videoVimeoRef,
    viewInRoomPhoto: viewInRoomPhotoRef,
}) => {
    const sortedPhoto = useFragment(sortedPhotoFragment, sortedPhotoRef);
    const videoVimeo = useFragment(videoVimeoFragment, videoVimeoRef);
    const viewInRoomPhoto = useFragment(viewInRoomPhotoFragment, viewInRoomPhotoRef);

    const { isActive, setActive } = useScrollCarouselItem();

    let child: ReactNode = null;
    let className = '';

    if (type === 'photo' && sortedPhoto) {
        const { masterOrZoomPath, alt } = sortedPhoto;
        const srcSet = getSrcSetStringXDescriptors({ url: masterOrZoomPath });
        child =
            masterOrZoomPath && alt && srcSet ? (
                <img className={styles.image} src={masterOrZoomPath} alt={alt} srcSet={srcSet} />
            ) : null;
    } else if (type === 'video' && videoVimeo) {
        const src = videoVimeo?.thumbnail;
        child = src ? (
            <>
                <img className={styles.image} src={src} alt="" />
                <div className={styles.videoThumbOverlay}>
                    <PlayButtonInverse className={styles.videoIcon} />
                </div>
            </>
        ) : null;
    } else if (type === 'viewInRoom' && viewInRoomPhoto) {
        const { src, credits } = viewInRoomPhoto;
        const srcSet = getSrcSetStringXDescriptors({ url: src });
        child =
            src && credits && srcSet ? (
                <>
                    <img className={styles.image} src={src} alt={credits} srcSet={srcSet} />
                    <div className={styles.viewInRoomOverlay}>
                        <ArtworkFrame className={styles.viewInRoomIcon} />
                    </div>
                </>
            ) : null;
    } else if (type === 'requestMore') {
        className = styles.requestMoreThumb;
        child = <Gallery className={styles.requestMoreIcon} />;
    }

    return (
        <Link
            className={classnames(styles.thumb, className, styles[size], {
                [styles.active]: isActive,
            })}
            onClick={setActive}
            dataTn="carousel-thumb"
        >
            {child}
        </Link>
    );
};
